import { queryOptions, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';

import { TourAvailabilityDocument } from './__generated__/tours.zeus';

export const DEFAULT_TOUR_DURATION_MINUTES = 30;

type TourAvailabilityParams = {
  listingId: string;
  daysOut?: number;
  durationMinutes?: number;
};

const tourAvailabilityQueryKey = ({
  listingId,
  daysOut,
  durationMinutes,
}: TourAvailabilityParams) =>
  ['tours', 'availability', listingId, daysOut, durationMinutes] as const;

const tourAvailabilityQueryOptions = (params: TourAvailabilityParams) =>
  queryOptions({
    queryKey: tourAvailabilityQueryKey(params),
    queryFn: async ({ signal }) => {
      const data = await requestZeusFromBrowser(
        TourAvailabilityDocument,
        { input: params },
        signal,
      );
      return data.tours.availability;
    },
    meta: { captureErrorContext: 'TourAvailability' },
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });

type UseTourAvailabilityOptions = Omit<TourAvailabilityParams, 'listingId'> & {
  enabled?: boolean;
};

export function useTourAvailability(
  listingId: string | null | undefined,
  { daysOut, durationMinutes, enabled = true }: UseTourAvailabilityOptions = {},
) {
  const query = useQuery({
    ...tourAvailabilityQueryOptions({ listingId: listingId ?? '', daysOut, durationMinutes }),
    enabled: enabled && Boolean(listingId),
  });
  return {
    availability: query.data ?? null,
    isLoading: query.isPending && query.fetchStatus !== 'idle',
    error: query.error,
    reload: query.refetch,
  };
}
